import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  Person,
  Bed,
  LocalHospital,
  CalendarToday,
  Description,
  AttachMoney,
  MedicalServices,
  Assignment,
  Timeline,
  Phone,
  AccessTime,
  CheckCircle,
  Warning,
  Info,
  ExpandMore,
  GroupAdd,
  History,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getICUAdmission,
  dischargeICUPatient,
  transferICUPatient,
  getAvailableICUBeds,
  getICUNurses,
  getICUActivityLogs,
  listICURounds,
} from "../../../../api/icu";
import {
  listLabOrders,
  listPatientXrays,
  listUltrasounds,
} from "../../../../api/ipd";
import MedicalEquipmentManager from "../../IPD/Admissions/MedicalEquipmentManager";
import PrescriptionsList from "../../../../Components/PrescriptionsList";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import useFetchObjects from "../../../../api/useFetchObjects";

const AdmissionView = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const navigate = useNavigate();
  const { id } = useParams();
  const apiBase = process.env.REACT_APP_API_URL || "";
  const mediaBase = apiBase.replace(/\/?api\/?$/, "");
  const toAbsolute = (url) => {
    if (!url) return url;
    return url.startsWith("http")
      ? url
      : `${mediaBase}${url.startsWith("/") ? "" : "/"}${url}`;
  };
  const [activeTab, setActiveTab] = useState(0);
  const [dischargeOpen, setDischargeOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [dischargeForm, setDischargeForm] = useState({
    discharge_diagnosis: "",
    discharge_notes: "",
    discharge_plan: {
      discharge_condition: "Stable",
      discharge_destination: "Home",
      follow_up_required: true,
      follow_up_date: "",
      follow_up_doctor: "",
      discharge_instructions: "",
    },
  });

  const [transferForm, setTransferForm] = useState({
    new_bed_id: "",
    transfer_reason: "",
    notes: "",
  });

  const queryClient = useQueryClient();

  // Fetch admission details
  const {
    data: admission,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["icu-admission", id],
    queryFn: () => getICUAdmission(id, token),
    enabled: !!id,
  });

  // Fetch nurse assignments
  const { data: nursesData } = useQuery({
    queryKey: ["icu-nurses", id],
    queryFn: () => getICUNurses(id, token),
    enabled: !!id,
  });

  // Fetch activity logs
  const { data: activityLogsData } = useQuery({
    queryKey: ["icu-activity-logs", id],
    queryFn: () => getICUActivityLogs(id, token),
    enabled: !!id,
  });

  // Fetch rounds
  const { data: roundsData } = useQuery({
    queryKey: ["icu-rounds", id],
    queryFn: () => listICURounds({ admission: id, page_size: 50 }, token),
    enabled: !!id,
  });

  // Investigations scoped to this admission
  const { data: labOrders } = useQuery({
    queryKey: ["lab-orders", id],
    queryFn: () => listLabOrders({ admission: id, page_size: 50 }, token),
    enabled: !!id,
  });
  const { data: xrays } = useQuery({
    queryKey: ["patient-xrays", id],
    queryFn: () => listPatientXrays({ admission: id, page_size: 50 }, token),
    enabled: !!id,
  });
  const { data: ultrasounds } = useQuery({
    queryKey: ["ultrasounds", id],
    queryFn: () => listUltrasounds({ admission: id, page_size: 50 }, token),
    enabled: !!id,
  });

  const [resultOpen, setResultOpen] = useState(false);
  const [resultPayload, setResultPayload] = useState(null);
  const tempChartRef = useRef(null);
  const hrChartRef = useRef(null);
  const spo2ChartRef = useRef(null);
  const bpChartRef = useRef(null);
  const rrChartRef = useRef(null);

  const roundsItems = useMemo(() => {
    const items = Array.isArray(roundsData)
      ? roundsData
      : roundsData?.results || [];
    return items
      .slice()
      .sort((a, b) => new Date(a.round_date) - new Date(b.round_date));
  }, [roundsData]);

  const buildSeries = (key) => {
    const labels = roundsItems.map((r) =>
      new Date(r.round_date).toLocaleString()
    );
    const values = roundsItems.map((r) => r[key] ?? null);
    return {
      labels,
      datasets: [
        {
          label: key,
          data: values,
          borderColor: "#1976d2",
          backgroundColor: "rgba(25,118,210,0.15)",
          tension: 0.3,
          pointRadius: 2,
          spanGaps: true,
        },
      ],
    };
  };

  const downloadChart = (ref, filename) => {
    try {
      const chart = ref.current;
      if (chart) {
        const url = chart.toBase64Image();
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
      }
    } catch (e) {}
  };

  // Fetch available beds for transfer
  const { data: availableBeds } = useQuery({
    queryKey: ["icu-available-beds"],
    queryFn: () => getAvailableICUBeds({}, token),
    enabled: transferOpen,
  });

  // Fetch doctors and departments for dialogs
  const { data: doctors } = useFetchObjects(
    ["users"],
    "users/?page_size=1000",
    token
  );

  const { data: departments } = useFetchObjects(
    ["departments"],
    "departments/?page_size=1000",
    token
  );

  const filteredDoctors = Array.isArray(doctors)
    ? doctors
    : doctors?.results || [];

  // Mutations
  const dischargeMutation = useMutation({
    mutationFn: (payload) => dischargeICUPatient(id, payload, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["icu-admission", id]);
      queryClient.invalidateQueries(["icu-dashboard"]);
      queryClient.invalidateQueries(["icu-available-beds"]);
      setDischargeOpen(false);
    },
  });

  const transferMutation = useMutation({
    mutationFn: (payload) => transferICUPatient(id, payload, token),
    onSuccess: () => {
      queryClient.invalidateQueries(["icu-admission", id]);
      queryClient.invalidateQueries(["icu-available-beds"]);
      setTransferOpen(false);
      setTransferForm({ new_bed_id: "", transfer_reason: "", notes: "" });
    },
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return "success";
      case 2:
        return "info";
      case 3:
        return "warning";
      case 4:
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return t("icu.admissions.status.admitted", "Admitted");
      case 2:
        return t("icu.admissions.status.discharged", "Discharged");
      case 3:
        return t("icu.admissions.status.transferred", "Transferred");
      case 4:
        return t("icu.admissions.status.absent", "Absent Without Leave");
      default:
        return t("icu.admissions.status.unknown", "Unknown");
    }
  };

  const getAdmissionTypeText = (type) => {
    switch (type) {
      case 1:
        return t("icu.admissions.type.emergency", "Emergency");
      case 2:
        return t("icu.admissions.type.post_operative", "Post-Operative");
      case 3:
        return t("icu.admissions.type.medical", "Medical");
      case 4:
        return t("icu.admissions.type.transfer_from_ipd", "Transfer from IPD");
      default:
        return t("icu.admissions.type.unknown", "Unknown");
    }
  };

  const getShiftText = (shift) => {
    switch (shift) {
      case 1:
        return t("icu.shifts.morning", "Morning");
      case 2:
        return t("icu.shifts.evening", "Evening");
      case 3:
        return t("icu.shifts.night", "Night");
      default:
        return "-";
    }
  };

  const getRoleText = (role) => {
    switch (role) {
      case 1:
        return t("icu.nurses.primary", "Primary");
      case 2:
        return t("icu.nurses.secondary", "Secondary");
      default:
        return "-";
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleDischarge = () => {
    dischargeMutation.mutate(dischargeForm);
  };

  const handleTransfer = () => {
    transferMutation.mutate(transferForm);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t("icu.admissions.error_loading", "Error loading admission")} -{" "}
          {error?.message}
        </Alert>
      </Box>
    );
  }

  if (!admission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          {t("icu.admissions.error_loading", "Error loading admission")}
        </Alert>
      </Box>
    );
  }

  const nurses = Array.isArray(nursesData)
    ? nursesData
    : nursesData?.results || [];
  const activityLogs = Array.isArray(activityLogsData)
    ? activityLogsData
    : activityLogsData?.results || [];
  const rounds = Array.isArray(roundsData)
    ? roundsData
    : roundsData?.results || [];

  return (
    <>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/icu/admissions")}
          >
            {t("common.back", "Back")}
          </Button>
          <Typography variant="h4" component="h1">
            {t("icu.admissions.admission_details", "ICU Admission Details")}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {admission.status === 1 && (
            <>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => setTransferOpen(true)}
                sx={{ mr: 1 }}
              >
                {t("icu.admissions.transfer_patient", "Transfer Patient")}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => setDischargeOpen(true)}
                sx={{ mr: 1 }}
              >
                {t("icu.admissions.discharge_patient", "Discharge Patient")}
              </Button>
            </>
          )}
        </Box>

        {/* Status Banner */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: "primary.light",
            color: "primary.contrastText",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: "primary.main" }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {admission.patient_name || "-"}
              </Typography>
              <Typography variant="body2">
                {t("icu.admissions.patient", "Patient")} •{" "}
                {admission.patient_phone || "-"}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Chip
              label={getStatusText(admission.status)}
              color={getStatusColor(admission.status)}
              variant="filled"
              sx={{ color: "white" }}
            />
          </Box>
        </Paper>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
          >
            <Tab
              label={t("icu.admissions.overview", "Overview")}
              icon={<Info />}
              iconPosition="start"
            />
            <Tab
              label={t("icu.admissions.nurse_assignments", "Nurse Assignments")}
              icon={<GroupAdd />}
              iconPosition="start"
            />
            <Tab
              label={t("icu.admissions.activity_logs", "Activity Logs")}
              icon={<History />}
              iconPosition="start"
            />
            <Tab
              label={t("icu.admissions.rounds", "Rounds")}
              icon={<Assignment />}
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <Box>
          {activeTab === 0 && (
            <Grid container spacing={3}>
              {/* Left Column - Patient & Admission Info */}
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("icu.admissions.patient_info", "Patient Information")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Person color="primary" />
                        <Typography variant="body2" color="text.secondary">
                          {t("icu.admissions.patient", "Patient")}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="bold">
                        {admission.patient_name || "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {admission.patient_phone || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Bed color="primary" />
                        <Typography variant="body2" color="text.secondary">
                          {t("icu.admissions.bed", "Bed")}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="bold">
                        {admission.bed_info || "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {admission.ward_name || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <LocalHospital color="primary" />
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "icu.admissions.admitting_doctor",
                            "Admitting Doctor"
                          )}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="bold">
                        {admission.admitting_doctor_name || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <MedicalServices color="primary" />
                        <Typography variant="body2" color="text.secondary">
                          {t("icu.admissions.department", "Department")}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="bold">
                        {admission.department_name || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Admission Details */}
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("icu.admissions.admission_details", "Admission Details")}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <CalendarToday color="primary" />
                        <Typography variant="body2" color="text.secondary">
                          {t("icu.admissions.admission_date", "Admission Date")}
                        </Typography>
                      </Box>
                      <Typography variant="body1" fontWeight="bold">
                        {admission.admission_date
                          ? new Date(
                              admission.admission_date
                            ).toLocaleDateString()
                          : "-"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {admission.admission_date
                          ? new Date(
                              admission.admission_date
                            ).toLocaleTimeString()
                          : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Assignment color="primary" />
                        <Typography variant="body2" color="text.secondary">
                          {t("icu.admissions.type_label", "Type")}
                        </Typography>
                      </Box>
                      <Chip
                        label={getAdmissionTypeText(admission.admission_type)}
                        color="info"
                        variant="outlined"
                      />
                    </Grid>
                    {admission.severity_score && (
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <Warning color="primary" />
                          <Typography variant="body2" color="text.secondary">
                            {t(
                              "icu.admissions.severity_score",
                              "Severity Score"
                            )}
                          </Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="bold">
                          {admission.severity_score}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Description color="primary" />
                        <Typography variant="body2" color="text.secondary">
                          {t("icu.admissions.diagnosis", "Diagnosis")}
                        </Typography>
                      </Box>
                      <Typography variant="body1">
                        {admission.admission_diagnosis || "-"}
                      </Typography>
                    </Grid>
                    {admission.admission_notes && (
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <Info color="primary" />
                          <Typography variant="body2" color="text.secondary">
                            {t("icu.admissions.notes", "Notes")}
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          {admission.admission_notes}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                {/* Ward Rounds History with Vitals Trends */}
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("icu.admissions.rounds", "Rounds")}
                  </Typography>
                  {/* Vitals Trends */}
                  {roundsItems.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t("icu.admissions.vitals_trends", "Vitals Trends")}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <Typography variant="body2">
                                Temperature (°C)
                              </Typography>
                              <Button
                                size="small"
                                onClick={() =>
                                  downloadChart(
                                    tempChartRef,
                                    `temperature-${id}.png`
                                  )
                                }
                              >
                                Download
                              </Button>
                            </Box>
                            <Line
                              ref={tempChartRef}
                              data={buildSeries("temperature")}
                              options={{
                                plugins: { legend: { display: false } },
                                scales: { x: { display: false } },
                              }}
                            />
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <Typography variant="body2">
                                Heart Rate (bpm)
                              </Typography>
                              <Button
                                size="small"
                                onClick={() =>
                                  downloadChart(
                                    hrChartRef,
                                    `heart-rate-${id}.png`
                                  )
                                }
                              >
                                Download
                              </Button>
                            </Box>
                            <Line
                              ref={hrChartRef}
                              data={buildSeries("heart_rate")}
                              options={{
                                plugins: { legend: { display: false } },
                                scales: { x: { display: false } },
                              }}
                            />
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <Typography variant="body2">SpO2 (%)</Typography>
                              <Button
                                size="small"
                                onClick={() =>
                                  downloadChart(spo2ChartRef, `spo2-${id}.png`)
                                }
                              >
                                Download
                              </Button>
                            </Box>
                            <Line
                              ref={spo2ChartRef}
                              data={buildSeries("oxygen_saturation")}
                              options={{
                                plugins: { legend: { display: false } },
                                scales: { x: { display: false } },
                              }}
                            />
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <Typography variant="body2">
                                Blood Pressure
                              </Typography>
                              <Button
                                size="small"
                                onClick={() =>
                                  downloadChart(bpChartRef, `bp-${id}.png`)
                                }
                              >
                                Download
                              </Button>
                            </Box>
                            <Line
                              ref={bpChartRef}
                              data={buildSeries("blood_pressure_systolic")}
                              options={{
                                plugins: { legend: { display: false } },
                                scales: { x: { display: false } },
                              }}
                            />
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              mb={1}
                            >
                              <Typography variant="body2">
                                Respiratory Rate (/min)
                              </Typography>
                              <Button
                                size="small"
                                onClick={() =>
                                  downloadChart(rrChartRef, `rr-${id}.png`)
                                }
                              >
                                Download
                              </Button>
                            </Box>
                            <Line
                              ref={rrChartRef}
                              data={buildSeries("respiratory_rate")}
                              options={{
                                plugins: { legend: { display: false } },
                                scales: { x: { display: false } },
                              }}
                            />
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  {/* Rounds List */}
                  {(() => {
                    const items = Array.isArray(roundsData)
                      ? roundsData
                      : roundsData?.results || [];
                    if (!items.length) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          {t("icu.admissions.no_rounds", "No rounds recorded")}
                        </Typography>
                      );
                    }
                    return (
                      <Grid container spacing={2}>
                        {items.map((r) => (
                          <Grid item xs={12} md={6} key={r.id}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2">
                                {new Date(r.round_date).toLocaleString()} —{" "}
                                {r.doctor_name}
                              </Typography>
                              {r.subjective && (
                                <Typography variant="body2">
                                  S: {r.subjective}
                                </Typography>
                              )}
                              {r.objective && (
                                <Typography variant="body2">
                                  O: {r.objective}
                                </Typography>
                              )}
                              {r.assessment && (
                                <Typography variant="body2">
                                  A: {r.assessment}
                                </Typography>
                              )}
                              {r.plan && (
                                <Typography variant="body2">
                                  P: {r.plan}
                                </Typography>
                              )}
                              <Grid container spacing={1} sx={{ mt: 1 }}>
                                {r.temperature && (
                                  <Grid item>
                                    <Typography variant="caption">
                                      Temp: {r.temperature}°C
                                    </Typography>
                                  </Grid>
                                )}
                                {r.blood_pressure_systolic &&
                                  r.blood_pressure_diastolic && (
                                    <Grid item>
                                      <Typography variant="caption">
                                        BP: {r.blood_pressure_systolic}/
                                        {r.blood_pressure_diastolic}
                                      </Typography>
                                    </Grid>
                                  )}
                                {r.heart_rate && (
                                  <Grid item>
                                    <Typography variant="caption">
                                      HR: {r.heart_rate} bpm
                                    </Typography>
                                  </Grid>
                                )}
                                {r.respiratory_rate && (
                                  <Grid item>
                                    <Typography variant="caption">
                                      RR: {r.respiratory_rate} /min
                                    </Typography>
                                  </Grid>
                                )}
                                {r.oxygen_saturation && (
                                  <Grid item>
                                    <Typography variant="caption">
                                      SpO2: {r.oxygen_saturation}%
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                              {r.nurses_present_names &&
                                r.nurses_present_names.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {t(
                                        "icu.rounds.nurses_present",
                                        "Nurses Present"
                                      )}
                                      : {r.nurses_present_names.join(", ")}
                                    </Typography>
                                  </Box>
                                )}
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    );
                  })()}
                </Paper>

                {/* Investigations */}
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("icu.admissions.investigations", "Investigations")}
                  </Typography>
                  {(() => {
                    const lab = Array.isArray(labOrders)
                      ? labOrders
                      : labOrders?.results || [];
                    const xr = Array.isArray(xrays)
                      ? xrays
                      : xrays?.results || [];
                    const us = Array.isArray(ultrasounds)
                      ? ultrasounds
                      : ultrasounds?.results || [];
                    if (!lab.length && !xr.length && !us.length) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "icu.admissions.no_investigations_found",
                            "No investigations found"
                          )}
                        </Typography>
                      );
                    }
                    return (
                      <List>
                        {/* Lab orders */}
                        {lab.map((o) => (
                          <ListItem
                            key={`lab-${o.id}`}
                            dense
                            secondaryAction={
                              <Button
                                size="small"
                                onClick={() => {
                                  setResultPayload({ type: "lab", data: o });
                                  setResultOpen(true);
                                }}
                              >
                                {t("icu.admissions.view_result", "View Result")}
                              </Button>
                            }
                          >
                            <ListItemText
                              primary={`${t(
                                "icu.admissions.lab_order",
                                "Lab Order"
                              )} #${o.id} — ${new Date(
                                o.order_date
                              ).toLocaleString()}`}
                              secondary={`${t(
                                "icu.admissions.tests",
                                "Tests"
                              )}: ${(o.tests_display || [])
                                .map(
                                  (t) =>
                                    t.test_type_display?.name ||
                                    t.test_type_display ||
                                    t.test_type_name ||
                                    ""
                                )
                                .filter(Boolean)
                                .join(", ")}`}
                            />
                          </ListItem>
                        ))}
                        {/* X-rays */}
                        {xr.map((x) => (
                          <ListItem
                            key={`xray-${x.id}`}
                            dense
                            secondaryAction={
                              <Button
                                size="small"
                                onClick={() => {
                                  setResultPayload({ type: "xray", data: x });
                                  setResultOpen(true);
                                }}
                              >
                                {t("icu.admissions.view_result", "View Result")}
                              </Button>
                            }
                          >
                            <ListItemText
                              primary={`${t(
                                "icu.admissions.xray",
                                "X-Ray"
                              )} — ${
                                x.xray_type?.name || x.xray_type_display || ""
                              }`}
                              secondary={`${new Date(
                                x.date
                              ).toLocaleDateString()} • ${
                                x.status_display || ""
                              }`}
                            />
                          </ListItem>
                        ))}
                        {/* Ultrasounds */}
                        {us.map((u) => (
                          <ListItem
                            key={`us-${u.id}`}
                            dense
                            secondaryAction={
                              <Button
                                size="small"
                                onClick={() => {
                                  setResultPayload({
                                    type: "ultrasound",
                                    data: u,
                                  });
                                  setResultOpen(true);
                                }}
                              >
                                {t("icu.admissions.view_result", "View Result")}
                              </Button>
                            }
                          >
                            <ListItemText
                              primary={`${t(
                                "icu.admissions.ultrasound",
                                "Ultrasound"
                              )} — ${
                                u.ultrasound_type?.name ||
                                u.ultrasound_type_display ||
                                ""
                              }`}
                              secondary={`${new Date(
                                u.date
                              ).toLocaleDateString()} • ${
                                u.status_display || ""
                              }`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    );
                  })()}
                </Paper>

                {/* Medical Equipment & Processes */}
                {/* Prescriptions Section */}
                <PrescriptionsList
                  patient={{ id: admission.patient }}
                  doctor={{ id: admission.admitting_doctor }}
                />

                <MedicalEquipmentManager
                  admissionId={admission.id}
                  patientName={admission.patient_name}
                  admissionType="icu"
                />
              </Grid>

              {/* Right Column - Financial & Actions */}
              <Grid item xs={12} md={4}>
                {/* Financial Summary */}
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("icu.admissions.financial_summary", "Financial Summary")}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <AttachMoney color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t(
                          "icu.admissions.total_charges",
                          "Total Charges"
                        )}
                        secondary={`${admission.total_charges || 0} AFN`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("icu.admissions.total_paid", "Total Paid")}
                        secondary={`${admission.total_paid || 0} AFN`}
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemIcon>
                        <Warning color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("icu.admissions.balance", "Balance")}
                        secondary={`${admission.balance || 0} AFN`}
                      />
                    </ListItem>
                  </List>
                </Paper>

                {/* Length of Stay */}
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("icu.admissions.length_of_stay", "Length of Stay")}
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {admission.length_of_stay || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("icu.admissions.days", "days")}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          {activeTab === 1 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("icu.admissions.nurse_assignments", "Nurse Assignments")}
              </Typography>
              {nurses.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("icu.admissions.no_nurses_assigned", "No nurses assigned")}
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {nurses.map((nurse) => (
                    <Grid item xs={12} md={6} key={nurse.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ bgcolor: "primary.main" }}>
                              <Person />
                            </Avatar>
                            <Box flexGrow={1}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {nurse.nurse_name || "-"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {getRoleText(nurse.role)} •{" "}
                                {getShiftText(nurse.shift)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {nurse.start_time
                                  ? new Date(nurse.start_time).toLocaleString()
                                  : "-"}
                              </Typography>
                            </Box>
                            <Chip
                              label={
                                nurse.is_active
                                  ? t("icu.nurses.active", "Active")
                                  : t("icu.nurses.inactive", "Inactive")
                              }
                              color={nurse.is_active ? "success" : "default"}
                              size="small"
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          )}

          {activeTab === 2 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("icu.admissions.activity_logs", "Activity Logs")}
              </Typography>
              {activityLogs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("icu.admissions.no_activities", "No activities recorded")}
                </Typography>
              ) : (
                <List>
                  {activityLogs.map((log) => (
                    <ListItem key={log.id} dense>
                      <ListItemIcon>
                        <Timeline color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={log.description || "-"}
                        secondary={
                          <>
                            <Typography variant="caption">
                              {log.user_name || "-"} •{" "}
                              {log.timestamp
                                ? new Date(log.timestamp).toLocaleString()
                                : "-"}
                            </Typography>
                            {log.notes && (
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {log.notes}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          )}

          {activeTab === 3 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("icu.admissions.rounds", "Rounds")}
              </Typography>
              {rounds.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("icu.admissions.no_rounds", "No rounds recorded")}
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {rounds.map((round) => (
                    <Grid item xs={12} md={6} key={round.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>
                            {round.round_date
                              ? new Date(round.round_date).toLocaleString()
                              : "-"}{" "}
                            • {round.doctor_name || "-"}
                          </Typography>
                          {round.subjective && (
                            <Typography variant="body2">
                              <strong>S:</strong> {round.subjective}
                            </Typography>
                          )}
                          {round.objective && (
                            <Typography variant="body2">
                              <strong>O:</strong> {round.objective}
                            </Typography>
                          )}
                          {round.assessment && (
                            <Typography variant="body2">
                              <strong>A:</strong> {round.assessment}
                            </Typography>
                          )}
                          {round.plan && (
                            <Typography variant="body2">
                              <strong>P:</strong> {round.plan}
                            </Typography>
                          )}
                          <Grid container spacing={1} sx={{ mt: 1 }}>
                            {round.temperature && (
                              <Grid item>
                                <Typography variant="caption">
                                  Temp: {round.temperature}°C
                                </Typography>
                              </Grid>
                            )}
                            {round.blood_pressure_systolic &&
                              round.blood_pressure_diastolic && (
                                <Grid item>
                                  <Typography variant="caption">
                                    BP: {round.blood_pressure_systolic}/
                                    {round.blood_pressure_diastolic}
                                  </Typography>
                                </Grid>
                              )}
                            {round.heart_rate && (
                              <Grid item>
                                <Typography variant="caption">
                                  HR: {round.heart_rate} bpm
                                </Typography>
                              </Grid>
                            )}
                            {round.respiratory_rate && (
                              <Grid item>
                                <Typography variant="caption">
                                  RR: {round.respiratory_rate} /min
                                </Typography>
                              </Grid>
                            )}
                            {round.oxygen_saturation && (
                              <Grid item>
                                <Typography variant="caption">
                                  SpO2: {round.oxygen_saturation}%
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                          {round.nurses_present_names &&
                            round.nurses_present_names.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {t(
                                    "icu.rounds.nurses_present",
                                    "Nurses Present"
                                  )}
                                  : {round.nurses_present_names.join(", ")}
                                </Typography>
                              </Box>
                            )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          )}
        </Box>
      </Box>

      {/* Result Dialog */}
      <Dialog
        open={resultOpen}
        onClose={() => setResultOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("icu.admissions.investigation_result", "Investigation Result")}
        </DialogTitle>
        <DialogContent dividers>
          {resultPayload?.type === "xray" && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {resultPayload.data?.xray_type?.name ||
                  resultPayload.data?.xray_type_display}
              </Typography>
              {resultPayload.data?.result_data ? (
                <Box>
                  {resultPayload.data.result_data?.image && (
                    <Box mb={2}>
                      <a
                        href={toAbsolute(resultPayload.data.result_data.image)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={toAbsolute(resultPayload.data.result_data.image)}
                          alt={t("icu.admissions.xray", "X-Ray")}
                          style={{
                            maxWidth: "100%",
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                        />
                      </a>
                    </Box>
                  )}
                  {resultPayload.data.result_data?.findings && (
                    <>
                      <Typography variant="body2" gutterBottom>
                        {t("icu.admissions.findings", "Findings")}:
                      </Typography>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="body2">
                          {resultPayload.data.result_data.findings}
                        </Typography>
                      </Paper>
                    </>
                  )}
                </Box>
              ) : (
                <Alert severity="info">
                  {t("icu.admissions.no_result_yet", "No result yet.")}
                </Alert>
              )}
            </>
          )}
          {resultPayload?.type === "ultrasound" && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {resultPayload.data?.ultrasound_type?.name ||
                  resultPayload.data?.ultrasound_type_display}
              </Typography>
              {resultPayload.data?.result_data ? (
                <Box>
                  {resultPayload.data.result_data?.image && (
                    <Box mb={2}>
                      <a
                        href={toAbsolute(resultPayload.data.result_data.image)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={toAbsolute(resultPayload.data.result_data.image)}
                          alt={t("icu.admissions.ultrasound", "Ultrasound")}
                          style={{
                            maxWidth: "100%",
                            borderRadius: 8,
                            cursor: "pointer",
                          }}
                        />
                      </a>
                    </Box>
                  )}
                  {resultPayload.data.result_data?.findings && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="body2">
                        {resultPayload.data.result_data.findings}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              ) : (
                <Alert severity="info">
                  {t("icu.admissions.no_result_yet", "No result yet.")}
                </Alert>
              )}
            </>
          )}
          {resultPayload?.type === "lab" && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                {t("icu.admissions.lab_order", "Lab Order")} #
                {resultPayload.data?.id}
              </Typography>
              {(resultPayload.data?.tests_display || []).map((t) => {
                const d = t.test_type_display || {};
                const hasRange =
                  d.reference_range ||
                  (d.min_value !== null &&
                    d.min_value !== undefined &&
                    d.max_value !== null &&
                    d.max_value !== undefined);
                return (
                  <Paper key={t.id} variant="outlined" sx={{ p: 2, mb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {d.name}
                    </Typography>
                    {hasRange && (
                      <Typography variant="caption" color="text.secondary">
                        Normal range:{" "}
                        {d.reference_range
                          ? d.reference_range
                          : `${d.min_value}–${d.max_value}${
                              d.units ? ` ${d.units}` : ""
                            }`}
                      </Typography>
                    )}
                    {d.result_value ? (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Result: {d.result_value}
                        {d.units ? ` ${d.units}` : ""}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t("icu.admissions.no_result_yet", "No result yet")}
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResultOpen(false)}>
            {t("common.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Discharge Dialog */}
      <Dialog
        open={dischargeOpen}
        onClose={() => setDischargeOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("icu.admissions.discharge_patient", "Discharge Patient")}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label={t(
              "icu.admissions.discharge_diagnosis",
              "Discharge Diagnosis"
            )}
            value={dischargeForm.discharge_diagnosis}
            onChange={(e) =>
              setDischargeForm({
                ...dischargeForm,
                discharge_diagnosis: e.target.value,
              })
            }
            multiline
            rows={3}
          />
          <TextField
            fullWidth
            margin="dense"
            label={t("icu.admissions.discharge_notes", "Discharge Notes")}
            value={dischargeForm.discharge_notes}
            onChange={(e) =>
              setDischargeForm({
                ...dischargeForm,
                discharge_notes: e.target.value,
              })
            }
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>
              {t("icu.admissions.follow_up_doctor", "Follow-up Doctor")}
            </InputLabel>
            <Select
              value={dischargeForm.discharge_plan.follow_up_doctor}
              label={t("icu.admissions.follow_up_doctor", "Follow-up Doctor")}
              onChange={(e) =>
                setDischargeForm({
                  ...dischargeForm,
                  discharge_plan: {
                    ...dischargeForm.discharge_plan,
                    follow_up_doctor: e.target.value,
                  },
                })
              }
            >
              <MenuItem value="">None</MenuItem>
              {filteredDoctors.map((doctor) => (
                <MenuItem key={doctor.id} value={doctor.id}>
                  {doctor.first_name} {doctor.last_name}
                  {doctor.username && ` (${doctor.username})`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDischargeOpen(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDischarge}
            disabled={dischargeMutation.isPending}
          >
            {dischargeMutation.isPending
              ? t("common.discharging", "Discharging...")
              : t("common.discharge", "Discharge")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("icu.admissions.transfer_patient", "Transfer Patient")}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>{t("icu.admissions.new_bed", "New Bed")}</InputLabel>
            <Select
              value={transferForm.new_bed_id}
              label={t("icu.admissions.new_bed", "New Bed")}
              onChange={(e) =>
                setTransferForm({ ...transferForm, new_bed_id: e.target.value })
              }
            >
              {availableBeds?.map((bed) => (
                <MenuItem key={bed.id} value={bed.id}>
                  {bed.bed_number} - {bed.room_number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="dense"
            label={t("icu.admissions.transfer_reason", "Transfer Reason")}
            value={transferForm.transfer_reason}
            onChange={(e) =>
              setTransferForm({
                ...transferForm,
                transfer_reason: e.target.value,
              })
            }
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleTransfer}
            disabled={transferMutation.isPending}
          >
            {transferMutation.isPending
              ? t("common.transferring", "Transferring...")
              : t("common.transfer", "Transfer")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdmissionView;
